// §8.3 quote request, waitlist and "email me the report" (PDF, see ./pdf.ts); shown only when VITE_LEAD_ENDPOINT is set.
// Backend: lead-worker/ (Cloudflare Worker + D1). Optional bot check: VITE_TURNSTILE_SITEKEY.
import { useEffect, useRef, useState } from 'preact/hooks';
import { fmt } from '../format';
import { t } from '../i18n';
import { CONTACT } from '../site/config';
import { track } from './analytics';
import { PDF_FORM_ID } from './pdf';
import type { Estimate, SizeOption } from './savings';
import type { ReportData } from './types';

const ENV = (import.meta as any).env ?? {};
const ENDPOINT: string | undefined = ENV.VITE_LEAD_ENDPOINT;
const SITEKEY: string | undefined = ENV.VITE_TURNSTILE_SITEKEY;
const APP_VERSION: string = ENV.VITE_APP_VERSION ?? '0.1.0';
export const leadEnabled = !!ENDPOINT;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

class PostError extends Error { constructor(public code: string) { super(code); } }

async function post(body: Record<string, unknown>, token: string): Promise<any> {
  const r = await fetch(ENDPOINT!, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...body, turnstile: token || undefined }) });
  if (!r.ok) {
    const code = await r.json().then((j: any) => String(j?.error ?? r.status)).catch(() => String(r.status));
    throw new PostError(code);
  }
  return r.json().catch(() => ({}));
}

// ---- Cloudflare Turnstile: script loads on the first interaction with a form, not on page load
let tsScript: Promise<any> | null = null;
function loadTurnstile(): Promise<any> {
  tsScript ??= new Promise((res, rej) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.onload = () => res((window as any).turnstile);
    s.onerror = () => { tsScript = null; rej(new Error('turnstile')); };
    document.head.append(s);
  });
  return tsScript;
}

function useTurnstile() {
  const box = useRef<HTMLDivElement>(null);
  const id = useRef<string | null>(null);
  const armed = useRef(false);
  useEffect(() => () => { if (id.current) (window as any).turnstile?.remove(id.current); }, []);
  const arm = () => {
    if (!SITEKEY || armed.current) return;
    armed.current = true;
    loadTurnstile().then((ts) => {
      if (box.current) id.current = ts.render(box.current, { sitekey: SITEKEY, appearance: 'interaction-only', size: 'flexible', language: 'sl' });
    }).catch(() => { armed.current = false; });
  };
  /** Token for the request; waits briefly while the invisible check finishes. */
  const token = async () => {
    if (!SITEKEY) return '';
    arm();
    for (let i = 0; i < 40; i++) {
      const v = id.current ? (window as any).turnstile?.getResponse(id.current) : '';
      if (v) return v as string;
      await new Promise((r) => setTimeout(r, 150));
    }
    return '';
  };
  const reset = () => { if (id.current) (window as any).turnstile?.reset(id.current); };
  const el = SITEKEY ? <div ref={box} class="ts" /> : null;
  return { arm, token, reset, el };
}

type State = 'idle' | 'sending' | 'ok' | 'fail';
const failText = (code: string | null) => (code === 'captcha' ? t.cta.captcha : t.cta.fail(CONTACT));

/** Fire `event` once when the element first becomes visible. */
function useSeen(event: Parameters<typeof track>[0]) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!ref.current || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { track(event); io.disconnect(); } }, { threshold: 0.4 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return ref;
}

export function LeadForm({ d, est, system }: { d: ReportData; est: Estimate | null; system?: SizeOption | null }) {
  const S = d.out.summary;
  const bp = S.pv_options[0];
  const [f, setF] = useState({ name: '', email: '', phone: '', note: '', consent: false });
  const [more, setMore] = useState(false);
  const [state, setState] = useState<State>('idle');
  const [code, setCode] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const started = useRef(false);
  const ts = useTurnstile();
  const seen = useSeen('cta_view');
  const errs = { name: !f.name.trim(), email: !EMAIL_RE.test(f.email.trim()), consent: !f.consent };
  const set = (k: keyof typeof f) => (e: Event) => {
    const el = e.target as HTMLInputElement;
    if (!started.current) { started.current = true; track('form_start'); }
    ts.arm();
    setF({ ...f, [k]: el.type === 'checkbox' ? el.checked : el.value });
  };
  const submit = async (e: Event) => {
    e.preventDefault();
    setTouched(true);
    if (errs.name || errs.email || errs.consent) return;
    setState('sending');
    try {
      await post({
        type: 'quote', region: d.region.id, ko: S.parcel.ko, parcel: S.parcel.st,
        contact: { name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim(), note: f.note.trim() },
        summary: {
          ground_annual_mean: S.ground.annual_mean,
          best_plane: bp ? { stavba: bp.stavba, smer: bp.smer, area_m2: bp.area_m2, kwp: bp.kwp, mwh_year: bp.mwh_year } : null,
          selected_planes: system?.planes?.map((p) => `${p.stavba} ${p.smer}`) ?? null,
          system_kwp: system?.kwp ?? null,
          estimate: est ? { savings_eur_year: Math.round(est.savingsYear), cost_eur: Math.round(est.cost), payback_y: est.paybackY && +est.paybackY.toFixed(1) } : null,
        },
        consent: true, created_at: new Date().toISOString(), app_version: APP_VERSION,
      }, await ts.token());
      setState('ok');
      track('quote_submitted');
    } catch (err) { setCode(err instanceof PostError ? err.code : null); setState('fail'); ts.reset(); }
  };
  return (
    <section id="ponudba" class="cta no-print" ref={seen as any}>
      <h2>{system ?? bp ? t.cta.titleKwp(fmt((system ?? bp)!.kwp, 1)) : t.cta.title}</h2>
      {state === 'ok' ? <p class="ok sheet" role="status">{t.cta.ok}</p> : (
        <div class="cta-grid">
          <form class="lead sheet" onSubmit={submit} noValidate>
            <p class="cap">{t.cta.lead}</p>
            <div class="grid2">
              <label>{t.cta.name}<input required value={f.name} onInput={set('name')} autoComplete="name" aria-invalid={touched && errs.name} />
                {touched && errs.name && <small class="err">{t.cta.required}</small>}</label>
              <label>{t.cta.email}<input required type="email" value={f.email} onInput={set('email')} autoComplete="email" aria-invalid={touched && errs.email} />
                {touched && errs.email && <small class="err">{t.cta.badEmail}</small>}</label>
            </div>
            {more ? (
              <div class="grid2">
                <label>{t.cta.phone} <span class="opt">{t.cta.optional}</span><input type="tel" value={f.phone} onInput={set('phone')} autoComplete="tel" /></label>
                <label>{t.cta.note} <span class="opt">{t.cta.optional}</span><input value={f.note} onInput={set('note')} /></label>
              </div>
            ) : <button type="button" class="linkbtn" onClick={() => setMore(true)}>{t.cta.more}</button>}
            <label class="check"><input type="checkbox" checked={f.consent} onChange={set('consent')} aria-invalid={touched && errs.consent} /> {t.cta.consent}</label>
            {touched && errs.consent && <small class="err">{t.cta.required}</small>}
            {ts.el}
            <div><button class="btn primary big" type="submit" disabled={state === 'sending'}>{state === 'sending' ? t.cta.sending : t.cta.submit}</button></div>
            {state === 'fail' && <p class="err" role="alert">{failText(code)}</p>}
          </form>
          <aside class="next">
            <h3>{t.cta.nextTitle}</h3>
            <ol>{t.cta.next.map((s) => <li key={s}>{s}</li>)}</ol>
          </aside>
        </div>
      )}
      <a class="sticky-cta" href="#ponudba">{t.cta.sticky}</a>
    </section>
  );
}

export function ReportPdfForm() {
  const [email, setEmail] = useState('');
  const [applicant, setApplicant] = useState({ name: '', address: '' });
  const [marketing, setMarketing] = useState(false);
  const [state, setState] = useState<State>('idle');
  const [gotPdf, setGotPdf] = useState(true);
  const [code, setCode] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const ts = useTurnstile();
  const bad = !EMAIL_RE.test(email.trim());
  const submit = async (e: Event) => {
    e.preventDefault(); setTouched(true);
    if (bad) return;
    setState('sending');
    try {
      const res = await post({ type: 'report_pdf', url: location.href, contact: { email: email.trim() }, marketing,
        applicant: { name: applicant.name.trim(), address: applicant.address.trim() }, created_at: new Date().toISOString(), app_version: APP_VERSION }, await ts.token());
      setGotPdf(res?.pdf !== false);
      setState('ok');
      track(res?.pdf === false ? 'report_emailed' : 'pdf_emailed');
    } catch (err) { setCode(err instanceof PostError ? err.code : null); setState('fail'); ts.reset(); }
  };
  return (
    <section id={PDF_FORM_ID} class="sendlink no-print">
      <div class="sheet">
        <h3>{t.reportPdf.title}</h3>
        {state === 'ok' ? <p class="ok" role="status">{gotPdf ? t.reportPdf.ok : t.reportPdf.okLink}</p> : (
          <form class="inline-form" onSubmit={submit} noValidate>
            <p class="cap">{t.reportPdf.lead}</p>
            <div class="row">
              <input type="email" value={email} placeholder={t.cta.email} aria-label={t.cta.email} autoComplete="email" aria-invalid={touched && bad}
                onInput={(e) => { setEmail((e.target as HTMLInputElement).value); ts.arm(); }} />
              <button class="btn primary" type="submit" disabled={state === 'sending'}>{state === 'sending' ? t.reportPdf.sending : t.reportPdf.submit}</button>
            </div>
            {state === 'sending' && <small class="cap" role="status">{t.reportPdf.slow}</small>}
            {touched && bad && <small class="err">{t.cta.badEmail}</small>}
            <details class="assume">
              <summary>{t.reportPdf.applicantTitle}</summary>
              <div class="grid2">
                <label>{t.reportPdf.applicantName}<input value={applicant.name} autoComplete="name" maxLength={120}
                  onInput={(e) => setApplicant({ ...applicant, name: (e.target as HTMLInputElement).value })} /></label>
                <label>{t.reportPdf.applicantAddress}<input value={applicant.address} autoComplete="street-address" maxLength={200}
                  onInput={(e) => setApplicant({ ...applicant, address: (e.target as HTMLInputElement).value })} /></label>
              </div>
              <small class="cap">{t.reportPdf.applicantNote}</small>
            </details>
            <label class="check small"><input type="checkbox" checked={marketing} onChange={(e) => setMarketing((e.target as HTMLInputElement).checked)} /> {t.reportPdf.marketing}</label>
            {ts.el}
            {state === 'fail' && <p class="err" role="alert">{failText(code)}</p>}
          </form>
        )}
      </div>
    </section>
  );
}

export function WaitlistForm({ region, ko, parcel, askObcina = false }: { region: string; ko: number; parcel: string; askObcina?: boolean }) {
  const [email, setEmail] = useState('');
  const [obcina, setObcina] = useState('');
  const [state, setState] = useState<State>('idle');
  const [code, setCode] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const ts = useTurnstile();
  if (!ENDPOINT) return null;
  const bad = !EMAIL_RE.test(email.trim());
  const submit = async (e: Event) => {
    e.preventDefault(); setTouched(true);
    if (bad) return;
    setState('sending');
    try {
      await post({ type: 'waitlist', region, ko, parcel, obcina: obcina.trim(), contact: { email: email.trim() }, created_at: new Date().toISOString(), app_version: APP_VERSION }, await ts.token());
      setState('ok');
      track('waitlist_submitted');
    } catch (err) { setCode(err instanceof PostError ? err.code : null); setState('fail'); ts.reset(); }
  };
  if (state === 'ok') return <p class="ok" role="status">{t.cta.waitlistOk}</p>;
  return (
    <form class="waitlist" onSubmit={submit} noValidate>
      {askObcina && (
        <label>{t.site.waitlist.obcina}<input value={obcina} placeholder={t.site.waitlist.obcinaPlaceholder} autoComplete="address-level2"
          onInput={(e) => { setObcina((e.target as HTMLInputElement).value); ts.arm(); }} /></label>
      )}
      <label>{t.cta.email}<input type="email" value={email} onInput={(e) => { setEmail((e.target as HTMLInputElement).value); ts.arm(); }} autoComplete="email" aria-invalid={touched && bad} /></label>
      {touched && bad && <small class="err">{t.cta.badEmail}</small>}
      {ts.el}
      <button class="btn primary" type="submit" disabled={state === 'sending'}>{t.cta.waitlistSubmit}</button>
      {state === 'fail' && <p class="err" role="alert">{failText(code)}</p>}
    </form>
  );
}
